import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PaperPlaneTilt, Sparkle, User, Robot, Copy, Check } from "@phosphor-icons/react";
import AIContent from "@/components/AIContent";

const SUGGESTIONS = [
  "O que é hora líquida?",
  "Como funciona a revisão espaçada?",
  "Dicas para redação do ENEM",
  "Como estudar exatas de forma eficiente?",
];

const msgId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function TutorPage() {
  const [messages, setMessages] = useState([
    { id: msgId(), role: "assistant", text: "Olá! Sou o Tutor Caminhos. 👋 Pergunte sobre técnicas de estudo, ambiente, revisão, ENEM ou qualquer conteúdo do livro. Estou aqui para ajudar." }
  ]);
  const [input, setInput] = useState("");
  const [provider, setProvider] = useState("openai");
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const endRef = useRef();

  async function copyMsg(m) {
    try {
      await navigator.clipboard.writeText(m.text);
      setCopiedId(m.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { toast.error("Não foi possível copiar"); }
  }

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  async function send(text) {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setMessages(m => [...m, { id: msgId(), role: "user", text: q }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: q, provider });
      setMessages(m => [...m, { id: msgId(), role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages(m => [...m, { id: msgId(), role: "assistant", text: "Erro ao contactar o tutor. Tente novamente." }]);
    } finally { setLoading(false); }
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-4xl mx-auto space-y-4 h-[calc(100vh-4rem)] lg:h-screen flex flex-col">
      <header className="fade-up">
        <div className="pill-badge bg-white text-[#1E5FFF] border border-[#1E5FFF]/20 mb-3">
          <Sparkle size={11} weight="fill" /> Tutor IA
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black text-[#12143A]">Tira-dúvidas com o Tutor</h1>
            <p className="text-zinc-500 text-sm mt-1">Baseado no livro <em>Os Caminhos para Aprovação</em>.</p>
          </div>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger data-testid="provider-select" className="w-full sm:w-48 h-11 rounded-2xl border-zinc-200"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-2xl">
              <SelectItem value="openai">GPT-4o Mini</SelectItem>
              <SelectItem value="gemini">Gemini 2.5 Flash</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="flex-1 bg-white rounded-3xl border border-[#1E5FFF]/15 shadow-[0_4px_16px_rgba(30,95,255,0.08)] p-4 sm:p-6 overflow-y-auto space-y-4" data-testid="chat-container">
        {messages.map((m) => (
          <div key={m.id} className={`flex gap-2 sm:gap-3 ${m.role === "user" ? "justify-end" : ""} group`}>
            {m.role === "assistant" && (
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl gradient-primary flex items-center justify-center flex-shrink-0 shadow-md">
                <Robot size={18} color="white" weight="fill" />
              </div>
            )}
            {m.role === "user" ? (
              <div className="max-w-[85%] sm:max-w-[75%] rounded-3xl rounded-br-lg px-4 py-3 text-sm leading-relaxed gradient-primary text-white shadow-[0_4px_16px_rgba(30,95,255,0.25)] whitespace-pre-wrap break-words">
                {m.text}
              </div>
            ) : (
              <div className="max-w-[85%] sm:max-w-[80%] relative rounded-3xl rounded-bl-lg px-4 sm:px-5 py-4 bg-white text-zinc-800 border border-zinc-100 shadow-sm break-words">
                <AIContent>{m.text}</AIContent>
                <button
                  data-testid={`copy-${m.id}`}
                  onClick={() => copyMsg(m)}
                  className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white border border-zinc-200 shadow-sm flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-zinc-50"
                  title="Copiar resposta"
                >
                  {copiedId === m.id ? <Check size={14} weight="bold" className="text-[#12B76A]" /> : <Copy size={13} weight="regular" className="text-zinc-500" />}
                </button>
              </div>
            )}
            {m.role === "user" && (
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl gradient-warm flex items-center justify-center flex-shrink-0 shadow-md">
                <User size={16} color="white" weight="fill" />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-2xl gradient-primary flex items-center justify-center flex-shrink-0"><Robot size={20} color="white" weight="fill" /></div>
            <div className="bg-white rounded-3xl rounded-bl-lg px-5 py-4 border border-zinc-100 shadow-sm">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#1E5FFF] animate-bounce" style={{animationDelay: '0ms'}}></span>
                <span className="w-2 h-2 rounded-full bg-[#1E5FFF] animate-bounce" style={{animationDelay: '150ms'}}></span>
                <span className="w-2 h-2 rounded-full bg-[#1E5FFF] animate-bounce" style={{animationDelay: '300ms'}}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2">
          {SUGGESTIONS.map(s => (
            <button key={s} onClick={() => send(s)}
                    className="text-xs font-semibold pill-badge bg-white border border-zinc-200 hover:border-[#1E5FFF] hover:text-[#1E5FFF] px-4 py-2">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input data-testid="chat-input" placeholder="Pergunte algo sobre estudos, técnicas, ENEM..."
               value={input} onChange={e => setInput(e.target.value)}
               onKeyDown={e => e.key === "Enter" && send()}
               className="h-14 rounded-2xl bg-white border-zinc-200 text-base focus-visible:ring-[#1E5FFF]/20 focus:border-[#1E5FFF]" />
        <Button data-testid="chat-send" onClick={() => send()} disabled={loading}
                className="h-14 w-14 rounded-2xl gradient-primary hover:opacity-90 text-white shadow-[0_8px_24px_rgba(30,95,255,0.35)]">
          <PaperPlaneTilt size={20} weight="fill" />
        </Button>
      </div>
    </div>
  );
}
